/**
 * Which client a request belongs to. The host decides, and only the host: a
 * browser-supplied tenant header is never read. The server sets x-tenant from
 * this answer, and the CMS trusts only that value.
 *
 * One answer per host:
 * - orkhan.art and www.orkhan.art serve the tenant orkhan-art (Orkhan's own site).
 * - A custom domain listed in NEXT_PUBLIC_TENANT_DOMAIN_MAP serves the tenant it names.
 * - <slug>.raisonne.work (and <slug>.localhost in development) serves <slug>.
 * - Any other host serves NEXT_PUBLIC_DEFAULT_TENANT_SLUG when that is set.
 * - Loopback hosts (local development, the server asking itself for health
 *   checks) serve orkhan-art, as they always have.
 * - Everything else serves no site at all (null), and is not Orkhan's.
 */

export const ORKHAN_TENANT = 'orkhan-art'

/** orkhan.art's own hosts. Keep in step with ORKHAN_HOST in next.config.mjs. */
export const ORKHAN_HOSTS = new Set(['orkhan.art', 'www.orkhan.art'])

/** The name older code imports. Same set. */
export const ART_HOSTS = ORKHAN_HOSTS

/**
 * The machine itself: `pnpm dev`, and requests the server makes to itself
 * (health checks). They have always been served as orkhan.art.
 * Keep in step with ORKHAN_HOST in next.config.mjs.
 */
export const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0'])

/** The platform domain: every client has <slug>.raisonne.work. */
export const PLATFORM_DOMAIN = 'raisonne.work'

export const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'cms',
  'control',
  'analytics',
  'design',
  'staging',
])

// .raisonne.art never had DNS; it stays readable so an old link still resolves to its client.
const TENANT_BASE_SUFFIXES = [`.${PLATFORM_DOMAIN}`, '.raisonne.art', '.localhost'] as const

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

export type TenantSlug = 'orkhan-art' | (string & {})

export function isValidTenantSlug(slug: string | null | undefined): slug is TenantSlug {
  if (!slug) return false
  const normalized = slug.trim().toLowerCase()
  return normalized.length > 0 && normalized.length <= 63 && SLUG_PATTERN.test(normalized)
}

/** True for Orkhan's own site only. No tenant (an unknown host) is not Orkhan. */
export function isOrkhanTenant(slug: string | null | undefined): boolean {
  return slug === ORKHAN_TENANT
}

/**
 * Orkhan's own pages, apps and drops. Another client's host answers 404 for these:
 * they are his work, his data, or they write into his records.
 */
export const ORKHAN_ONLY_PREFIXES = [
  '/portal',
  '/portal-training',
  '/portal-suspended',
  '/conversations',
  '/conversations-sim',
  '/untitled',
  '/visions',
  '/savingaesthetics',
  '/tisa',
  '/tisa-2',
  '/memememory',
  '/immersive-preview',
  '/collector/claim',
  '/collector/rewards',
  '/guild',
  '/collector-waitlist',
  // /insights/guild is the tenant-scoped public tiers/badges page.
  // The personal /guild, claim and reward workflows above remain Orkhan-only.
  // Static drops in public/ (and their external origins, see next.config.mjs).
  '/kilim',
  '/datadune',
  '/matter',
  '/raffle',
  // The fortune sphere, a gift for fal, and its calls to fal (src/app/fal/api).
  '/fal',
  // Files of his hand-built rooms and his showreel.
  '/immersive',
  '/media/portal',
  '/media/relic',
  '/media/showreel',
  // Tells the domain worker that an alias reaches orkhan.art.
  '/site-health.json',
  // orkhan.art's install manifest (its name, its icons). Other sites declare none.
  '/site.webmanifest',
  // API routes of his own apps.
  '/api/untitled',
  '/api/visions',
  '/api/conversations',
] as const

export function isOrkhanOnlyRoute(pathname: string): boolean {
  return ORKHAN_ONLY_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

export function defaultDesignForTenant(slug: string | null | undefined): string {
  if (isOrkhanTenant(slug)) return 'oma-original'
  return process.env.NEXT_PUBLIC_DEFAULT_DESIGN_ID?.trim() || 'skin-zero'
}

/** A readable name from a slug, for when the CMS has none. */
export function formatTenantName(slug: string | null | undefined): string {
  if (isOrkhanTenant(slug)) {
    return 'Orkhan Mammadov'
  }
  if (!slug) return 'Artist'
  return slug
    .trim()
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** The host without port or trailing dot, lowercased. "[::1]:3000" stays "[::1]". */
export function bareHost(host: string | null | undefined): string {
  const raw = (host ?? '').trim().toLowerCase()
  if (!raw) return ''
  if (raw.startsWith('[')) {
    const end = raw.indexOf(']')
    return end > 0 ? raw.slice(0, end + 1) : raw
  }
  return raw.split(':')[0].replace(/\.$/, '')
}

function parseTenantDomainMap(rawMap: string | undefined): Map<string, TenantSlug> {
  const map = new Map<string, TenantSlug>()
  const trimmed = rawMap?.trim()
  if (!trimmed) return map

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [domain, slug] of Object.entries(parsed)) {
          const cleanDomain = domain.trim().toLowerCase()
          const cleanSlug = typeof slug === 'string' ? slug.trim().toLowerCase() : ''
          if (cleanDomain && isValidTenantSlug(cleanSlug)) {
            map.set(cleanDomain, cleanSlug)
          }
        }
        return map
      }
    } catch {
      // Fall through to comma-separated parser
    }
  }

  for (const entry of trimmed.split(',')) {
    const pair = entry.trim()
    if (!pair) continue
    const eqIndex = pair.indexOf('=')
    if (eqIndex <= 0) continue
    const cleanDomain = pair.slice(0, eqIndex).trim().toLowerCase()
    const cleanSlug = pair.slice(eqIndex + 1).trim().toLowerCase()
    if (cleanDomain && isValidTenantSlug(cleanSlug)) {
      map.set(cleanDomain, cleanSlug)
    }
  }

  return map
}

function extractSubdomainTenant(bareHostname: string): TenantSlug | null {
  for (const suffix of TENANT_BASE_SUFFIXES) {
    if (bareHostname.endsWith(suffix)) {
      const prefix = bareHostname.slice(0, -suffix.length).replace(/^www\./, '')
      if (!prefix || prefix.includes('.')) return null
      if (RESERVED_SUBDOMAINS.has(prefix)) return null
      if (isValidTenantSlug(prefix)) return prefix
      return null
    }
  }
  return null
}

export function tenantFor(host: string | null | undefined, _pathname = ''): TenantSlug | null {
  const raw = (host ?? '').trim().toLowerCase()
  const bare = bareHost(raw)

  // 1. Orkhan's own hosts
  if (ORKHAN_HOSTS.has(bare)) {
    return ORKHAN_TENANT
  }

  // 2. Custom domain mappings from NEXT_PUBLIC_TENANT_DOMAIN_MAP
  const domainMap = parseTenantDomainMap(process.env.NEXT_PUBLIC_TENANT_DOMAIN_MAP)
  if (domainMap.size > 0 && bare) {
    const withoutWww = bare.replace(/^www\./, '')
    const mapped = domainMap.get(raw) ?? domainMap.get(bare) ?? domainMap.get(withoutWww)
    if (mapped) {
      return mapped
    }
  }

  // 3. Subdomains of .raisonne.work (or .raisonne.art, or .localhost in development)
  if (bare) {
    const subTenant = extractSubdomainTenant(bare)
    if (subTenant) {
      return subTenant
    }
  }

  // 4. NEXT_PUBLIC_DEFAULT_TENANT_SLUG, when the server is told what an unmatched host serves
  const defaultEnv = process.env.NEXT_PUBLIC_DEFAULT_TENANT_SLUG?.trim().toLowerCase()
  if (defaultEnv && isValidTenantSlug(defaultEnv)) {
    return defaultEnv
  }

  // 5. The machine itself: development and the server's own health checks
  if (LOOPBACK_HOSTS.has(bare)) {
    return ORKHAN_TENANT
  }

  // 6. A host no client owns
  return null
}

/** Is this host one of Orkhan's (it resolves to orkhan-art)? */
export function isOrkhanHost(host: string | null | undefined): boolean {
  return isOrkhanTenant(tenantFor(host))
}

/** orkhan.art's canonical origin, as it has always been configured. */
export function orkhanSiteUrl(): string {
  return (process.env.NEXT_PUBLIC_SITE_URL || 'https://orkhan.art').replace(/\/+$/, '')
}

export interface SiteAddress {
  /** Where the site's own links, canonical tags and sitemap point, without a trailing slash. */
  origin: string
  /** The domain shown to visitors, without www or port. */
  domain: string
}

/**
 * The address of the site a request is for. orkhan.art keeps its configured origin. Another
 * client's site is addressed by the host the visitor used (port kept on development hosts), and by
 * <slug>.raisonne.work when there is no host to go by.
 */
export function siteAddressFor(
  host: string | null | undefined,
  slug: string | null | undefined = tenantFor(host)
): SiteAddress {
  if (isOrkhanTenant(slug)) {
    return { origin: orkhanSiteUrl(), domain: 'orkhan.art' }
  }

  const raw = (host ?? '').trim().toLowerCase()
  const bare = bareHost(raw)
  if (bare && slug && tenantFor(raw) === slug) {
    const local = bare === 'localhost' || bare.endsWith('.localhost') || LOOPBACK_HOSTS.has(bare)
    const hostWithPort = /^[a-z0-9.\-[\]:]+$/.test(raw) ? raw : bare
    return {
      origin: local ? `http://${hostWithPort}` : `https://${bare}`,
      domain: bare.replace(/^www\./, ''),
    }
  }

  const fallback = slug && isValidTenantSlug(slug) ? `${slug}.${PLATFORM_DOMAIN}` : PLATFORM_DOMAIN
  return { origin: `https://${fallback}`, domain: fallback }
}
